import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import CircularProgress from '@mui/material/CircularProgress';
import DataErrorComponent from './DataErrorComponent';

interface Column {
  columnId: string;
  columnName: string;
}

interface TrendChartComponentProps {
  chartData: any[];
  metadata: Column[];
}

const TrendChartComponent: React.FC<TrendChartComponentProps> = ({
  chartData,
  metadata,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const isLoading = false;
  const error = null;
  useEffect(() => {
    resizeObserver.current = new ResizeObserver((entries) => {
      entries.forEach(({ target }) => {
        const instance = echarts.getInstanceByDom(target as HTMLDivElement);
        if (instance) {
          instance.resize();
        }
      });
    });
  }, []);

  useEffect(() => {
    if (chartRef.current && resizeObserver.current) {
      const chart = echarts.init(chartRef.current);

      const datePoints = chartData.map((item) => item.Date);
      const updatedSeriesData = (metadata
        ?.filter((column) => column.columnId !== 'Date')
        .map((column) => ({
          name: column.columnName,
          type: 'line',
          data: chartData.map((item) => item[column.columnId]),
        })) || []) as echarts.EChartOption.Series[];

      const dataZoomSlider = [
        {
          type: 'slider',
          start: 0,
          end: 100,
          bottom: 10,
          labelFormatter: function (value, valueStr) {
            if (!valueStr) return valueStr;
            const date = new Date(valueStr);
            return date.toLocaleDateString();
          },
        },
      ];

      const options: echarts.EChartOption = {
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            const parsedDate = new Date(params[0].axisValue);
            const dateLabel = parsedDate.toLocaleDateString();
            const seriesInfo = params
              .map((param: any) => {
                return `<div class="trend-chart-component-tooltip-container">
                          <div class="trend-chart-component-tooltip-item" style="background-color: ${param.color}; "></div>
                          <span>${param.seriesName}: <b>${param.value}</b></span>
                        </div>`;
              })
              .join('');
            return `<div class="trend-chart-component-tooltip-content"><b style="color: #1976d2;">${dateLabel}</b></div>${seriesInfo}`;
          },
        },
        grid: { left: '40', right: '40', top: '40' },
        xAxis: {
          type: 'category',
          data: datePoints,
          axisLabel: {
            formatter: (value: any) => {
              const date = new Date(value);
              return date.toLocaleDateString();
            },
          },
        },
        yAxis: {
          type: 'value',
        },
        legend: {
          type: 'scroll',
          data: updatedSeriesData.map((series) => series.name) as string[],
        },
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100,
          },
          ...dataZoomSlider,
        ],
        series: updatedSeriesData,
      };

      chart.setOption(options, true);
      resizeObserver.current.observe(chartRef.current);
    }
  }, [chartData, chartRef, metadata]);

  return (
    <div className="dashboard-pod-control-container">
      {isLoading ? (
        <div className="grid-loader">
          <CircularProgress />
        </div>
      ) : error ? (
        <DataErrorComponent error={error} />
      ) : (
        <div
          className="trend-chart-component-container"
          style={{
            display: isLoading ? 'none' : 'block',
          }}
        >
          <div ref={chartRef} className="trend-chart-component-container" />
        </div>
      )}
    </div>
  );
};

export default TrendChartComponent;
